/**
 * Sub-aba "Pessoas Indicação": quem indica clientes sem ter conta de cliente. Porte de
 * `admin.html:2101-2114` e `7047-7122`; linhas 70 a 73 do inventário. A mesma tabela alimenta o
 * "Indicado por" do Novo lead, em Vendas.
 *
 * UM ÚNICO "SALVAR ALTERAÇÕES" NO RODAPÉ desde 07/10/2026, por pedido do fundador aceito em
 * enquete: as sub-abas de Configuração com lista editável trocam o "Salvar" por linha por um botão
 * só. O legado tem um por linha (`admin.html:7076`), e renomear três pessoas custava três cliques e
 * três idas ao banco. O "Excluir" FICA na linha, porque excluir é ação sobre um registro, e não
 * edição de campo: juntá-lo ao botão do rodapé faria um clique apagar gente.
 *
 * DEFEITO DO LEGADO CORRIGIDO EM 07/10/2026, por decisão do fundador ("corrigir erro"), depois de
 * ele ver na tela o "Erro ao excluir: invalid input syntax for type uuid: NaN".
 *
 * O QUE O LEGADO FAZ, e esta tela fazia igual até hoje: ele lê o id do botão com
 * `parseInt(btn.dataset.id, 10)` (`admin.html:7087` e `7101`), e o id é um uuid, que começa por
 * letra na maioria das vezes. O resultado era `NaN`, e daí saíam os dois defeitos que a pessoa via:
 *   - "Salvar": NADA acontecia na tela, porque a linha era procurada por um id que nunca casa.
 *   - "Excluir": o banco recusava o filtro `id=eq.NaN` por não ser uuid, e o aviso mostrava a
 *     mensagem crua do Postgres.
 *
 * Agora os dois gestos usam o identificador inteiro. A chave `REPRODUZIR_PARSEINT_DO_ID` continua
 * no arquivo, em `false`, como registro da decisão.
 */

// Estado, memória, referência e efeito do React.
import { useCallback, useEffect, useRef, useState } from 'react';
// As peças da plataforma.
import { Botao, Campo } from '@complexity/ui';
// As chamadas da área.
import {
  excluirPessoaIndicacao,
  inserirPessoaIndicacao,
  listarPessoasIndicacao,
  renomearPessoaIndicacao,
} from '@studiolu/dados/areas/sistema';
// A linha de `indicadores_externos`.
import type { LinhaIndicadoresExternos } from '@studiolu/dados';
// Sessão e fonte de dados.
import { usePainel } from '../../contexto.js';
// O parágrafo do status, desenhado como o legado.
import { LinhaDeStatus } from '../../comum/status-temporario.js';
// A memória da área, e o status que some em 3 s (`mostrarStatusPessoaIndicacao`, `admin.html:7057`).
import { useMemoria, useStatusDaArea } from './memoria.js';
// A linha compacta dos campos curtos.
import { LinhaCompacta } from '../financeiro/apoio.js';
// As peças visuais da área.
import { BotaoDaLinha, CartaoParam, LinhaParam, NotaSecao, TextoParam, type PropsDaSubAba } from './pecas.js';

/**
 * `true` reproduz o `parseInt(btn.dataset.id, 10)` do legado sobre o id uuid (`admin.html:7087` e
 * `7101`). Com `false`, os dois gestos funcionam: o "Salvar" acha a linha e grava, e o "Excluir"
 * manda o identificador inteiro.
 *
 * **PASSOU A SER `false` EM 07/10/2026, POR DECISÃO DO FUNDADOR.** Ele abriu a tela, clicou em
 * EXCLUIR, leu *"Erro ao excluir: invalid input syntax for type uuid: NaN"* e mandou: *"corrigir
 * erro"*. Era o defeito do legado chegando na tela dele exatamente como a migração prometia, e a
 * decisão de mantê-lo (19/09/2026) foi revogada por ele agora.
 *
 * A CHAVE FICA, e não vira código apagado: ela é o registro de que isto foi uma escolha, e não um
 * descuido, e é o que permite comparar com o admin atual quando alguém perguntar por que os dois
 * se comportam diferente.
 */
const REPRODUZIR_PARSEINT_DO_ID = false;

/** O id como o legado o lê do botão: o `parseInt` de um uuid dá `3` (se começa por dígitos) ou `NaN`. */
function idComoOLegadoLe(id: string): string {
  // `String(NaN)` é "NaN", e é assim que o cliente do Supabase escreve o `NaN` do legado no filtro.
  return REPRODUZIR_PARSEINT_DO_ID ? String(parseInt(id, 10)) : id;
}

// A ordem alfabética `pt-BR` do legado depois de adicionar (`admin.html:7118`).
function porNome(a: LinhaIndicadoresExternos, b: LinhaIndicadoresExternos): number {
  return a.nome.localeCompare(b.nome, 'pt-BR');
}

export function SubAbaPessoasIndicacao({ confirmar, alertar, recarga }: PropsDaSubAba) {
  const { dados } = usePainel();
  // `null` enquanto lê: o "Carregando…" do legado (`admin.html:7052`).
  const [pessoas, setPessoas] = useState<readonly LinhaIndicadoresExternos[] | null>(null);
  // A última lista lida (`indicadoresExternosCarregados`): falha de leitura mantém a anterior.
  const carregadas = useRef<readonly LinhaIndicadoresExternos[]>([]);
  // O texto de cada campo de nome, que o legado redesenha a partir da lista a cada mudança dela.
  const [nomes, setNomes] = useState<Readonly<Record<string, string>>>({});
  /**
   * O QUE O BANCO TEM, guardado à parte do que está na tela, por id.
   *
   * É a diferença entre os dois que diz quais nomes precisam ser gravados. O mesmo desenho das
   * sub-abas "Visibilidade Aba" e "Preço Padrão Produto", pelo mesmo motivo: sem o retrato, o botão
   * único gravaria a lista inteira a cada clique, e N escritas para mudar um nome é desperdício que
   * o banco paga e que suja o histórico de quem olhar depois.
   *
   * ELE EXISTE À PARTE DE `carregadas`, que é referência e não dispara desenho: a contagem do
   * rodapé precisa mudar a cada tecla, e isso só acontece com estado.
   */
  const [original, setOriginal] = useState<Readonly<Record<string, string>>>({});
  // Trava o botão durante a gravação: clicar duas vezes gravaria a mesma coisa duas vezes.
  const [salvando, setSalvando] = useState(false);
  // O campo "Nome" do formulário de adicionar, na memória da área: no legado é o `input` fixo
  // `#param-nova-pessoa-indicacao` (`admin.html:2104`), que a ida a outra aba não limpa. Os campos
  // de nome DENTRO da lista ficam de fora de propósito: o legado os redesenha a cada leitura.
  const [novoNome, setNovoNome] = useMemoria('sistema:pessoas:novo-nome', '');
  // O parágrafo `#param-pessoa-indicacao-status`, fora da lista no legado (`admin.html:2112`).
  const [status, mostrarStatus] = useStatusDaArea('sistema:pessoas:status');

  // Redesenha a lista: os campos voltam ao nome guardado, como o `innerHTML` do legado.
  const desenhar = useCallback((lista: readonly LinhaIndicadoresExternos[]) => {
    carregadas.current = lista;
    const naTela = Object.fromEntries(lista.map((p) => [p.id, p.nome]));
    setNomes(naTela);
    // O mesmo retrato guardado como "o que o banco tem": a partir daqui, diferença é alteração.
    setOriginal(naTela);
    setPessoas(lista);
  }, []);

  // `carregarPessoasIndicacaoParam` (`admin.html:7050-7055`).
  const carregar = useCallback(async () => {
    setPessoas(null);
    const resultado = await listarPessoasIndicacao(dados);
    // Falha só vai ao console, e a lista anterior fica (`admin.html:7043`).
    if (!resultado.ok) console.error('Erro ao carregar indicadores externos:', resultado.erro.mensagem);
    desenhar(resultado.ok ? (resultado.dados ?? []) : carregadas.current);
  }, [dados, desenhar]);

  // Lê ao abrir a área e a cada clique na sub-aba.
  useEffect(() => {
    void carregar();
  }, [carregar, recarga]);

  /**
   * AS PESSOAS CUJO NOME MUDOU, comparando o campo na tela com o retrato do banco.
   *
   * A COMPARAÇÃO É DO NOME APARADO nas duas pontas, e não do texto cru, porque é o nome aparado que
   * vai ao banco (`.trim()` do legado, `admin.html:7079`). Sem aparar, digitar um espaço no fim
   * acenderia o botão para uma gravação que não mudaria linha nenhuma.
   */
  const alterados = (pessoas ?? []).filter((p) => (nomes[p.id] ?? '').trim() !== (original[p.id] ?? '').trim());

  /**
   * "Salvar alterações": UM botão para a lista inteira, por pedido do fundador em 07/10/2026,
   * aceito em enquete, no mesmo desenho das sub-abas "Visibilidade Aba" e "Preço Padrão Produto".
   *
   * MUDA O LEGADO DE PROPÓSITO, e ele decidiu: o admin atual tem um "Salvar" por linha
   * (`admin.html:7076` e `7085-7097`).
   *
   * A GRAVAÇÃO É UMA DE CADA VEZ, e PARA na primeira falha, nomeando a pessoa: a função de banco
   * renomeia uma por chamada, e continuar depois de um erro deixaria a tela dizendo "salvo" com
   * metade gravada. O nome dito é o do BANCO, e não o que está no campo: o campo pode estar vazio,
   * que é justamente um dos casos de parada.
   */
  const salvarTudo = useCallback(async () => {
    if (alterados.length === 0) return;
    setSalvando(true);
    let gravados = 0;
    for (const alterada of alterados) {
      const id = idComoOLegadoLe(alterada.id);
      // A linha é procurada pelo id LIDO; com o `parseInt`, ela nunca é achada.
      const pessoa = carregadas.current.find((p) => p.id === id);
      if (!pessoa) {
        // O `TypeError` do legado: nada na tela, só o console. A sequência para aqui, porque seguir
        // terminaria com "salvo" numa tela onde esta linha não foi gravada.
        console.error(new TypeError("Cannot read properties of null (reading 'querySelector')"));
        setSalvando(false);
        return;
      }
      const nome = (nomes[pessoa.id] ?? '').trim();
      if (!nome) {
        setSalvando(false);
        // A frase do legado (`admin.html:7081`) com a pessoa entre parênteses: com a lista inteira
        // num botão só, "Nome não pode ficar vazio." sozinho obriga a adivinhar qual campo é.
        mostrarStatus(`Nome não pode ficar vazio ("${original[pessoa.id] ?? pessoa.nome}").`);
        return;
      }
      const resultado = await renomearPessoaIndicacao(dados, id, nome);
      if (!resultado.ok) {
        setSalvando(false);
        mostrarStatus(`Erro ao salvar "${original[pessoa.id] ?? pessoa.nome}": ${resultado.erro.mensagem}`);
        return;
      }
      // O nome novo em memória, sem redesenhar (`admin.html:7093-7094`).
      carregadas.current = carregadas.current.map((p) => (p.id === id ? { ...p, nome } : p));
      // O retrato avança junto: a linha gravada sai da contagem, mesmo se a próxima falhar.
      setOriginal((atuais) => ({ ...atuais, [pessoa.id]: nome }));
      gravados++;
    }
    setSalvando(false);
    // "Salvo!" é a palavra do legado, e fica no caso de uma só. Mais de uma, o número aparece.
    mostrarStatus(gravados === 1 ? 'Salvo!' : `${gravados} nomes salvos.`);
  }, [alterados, nomes, original, dados, mostrarStatus]);

  // "Excluir" de uma pessoa (`admin.html:7099-7108`).
  const excluir = useCallback(
    async (idDaLinha: string) => {
      const id = idComoOLegadoLe(idDaLinha);
      if (!(await confirmar('Excluir essa pessoa? Leads que já apontam pra ela ficam sem indicador registrado.'))) return;
      const resultado = await excluirPessoaIndicacao(dados, id);
      if (!resultado.ok) {
        await alertar('Erro ao excluir: ' + resultado.erro.mensagem);
        return;
      }
      desenhar(carregadas.current.filter((p) => p.id !== id));
    },
    [dados, confirmar, alertar, desenhar],
  );

  // "Adicionar" (`admin.html:7111-7122`).
  const adicionar = useCallback(async () => {
    const nome = novoNome.trim();
    if (!nome) {
      mostrarStatus('Escreva um nome.');
      return;
    }
    const resultado = await inserirPessoaIndicacao(dados, nome);
    if (!resultado.ok) {
      mostrarStatus('Erro ao adicionar: ' + resultado.erro.mensagem);
      return;
    }
    // Entra na lista, volta à ordem alfabética e a lista é redesenhada.
    desenhar([...carregadas.current, resultado.dados].sort(porNome));
    setNovoNome('');
    mostrarStatus('Adicionado!');
  }, [novoNome, dados, desenhar, mostrarStatus, setNovoNome]);

  return (
    <div className="flex flex-col gap-cx-4">
      <NotaSecao>
        Pessoas que indicam clientes mas não têm conta de cliente própria, cadastradas aqui ou criadas na hora ao escolher
        &quot;Nenhum dos acima&quot; no campo &quot;Indicado por&quot; de um novo lead.
      </NotaSecao>
      <LinhaCompacta>
        <Campo
          rotulo="Nome"
          type="text"
          name="param-nova-pessoa-indicacao"
          autoComplete="name"
          placeholder="Nome da pessoa"
          value={novoNome}
          onChange={(evento) => setNovoNome(evento.target.value)}
        />
        <Botao onClick={() => void adicionar()}>Adicionar</Botao>
      </LinhaCompacta>
      <LinhaDeStatus texto={status} testid="status-pessoas" />

      {pessoas === null && <TextoParam>Carregando…</TextoParam>}
      {pessoas !== null && pessoas.length === 0 && <TextoParam>Nenhuma pessoa cadastrada ainda.</TextoParam>}
      {pessoas !== null && pessoas.length > 0 && (
        <>
          <CartaoParam testid="lista-pessoas">
            {pessoas.map((pessoa) => (
              <LinhaParam key={pessoa.id} data-id={pessoa.id}>
                {/* O nome editável na própria linha, ocupando o que sobra (`admin.html:7074`). */}
                <input
                  type="text"
                  autoComplete="name"
                  aria-label={`Nome de ${pessoa.nome}`}
                  className="cx-campo min-w-0 flex-1"
                  value={nomes[pessoa.id] ?? ''}
                  onChange={(evento) => {
                    const valor = evento.target.value;
                    setNomes((atuais) => ({ ...atuais, [pessoa.id]: valor }));
                  }}
                />
                {/* Só o "Excluir" sobra na linha: o "Salvar" por linha saiu em 07/10/2026, e
                    excluir continua aqui porque é ação sobre este registro, com confirmação. */}
                <span className="ml-auto flex shrink-0 items-center">
                  <BotaoDaLinha perigo onClick={() => void excluir(pessoa.id)}>Excluir</BotaoDaLinha>
                </span>
              </LinhaParam>
            ))}
          </CartaoParam>

          {/*
            UM BOTÃO PARA A LISTA INTEIRA, no rodapé à direita, igual ao das sub-abas "Visibilidade
            Aba" e "Preço Padrão Produto": é onde a ação que fecha um formulário mora em toda
            plataforma grande, e é o último lugar por onde o olho passa depois de percorrer as
            linhas.

            ELE DIZ QUANTOS NOMES VAI GRAVAR, e não só "Salvar": com a lista inteira num botão só, a
            pessoa precisa saber se o que ela digitou foi registrado antes de clicar. Sem alteração
            ele fica desligado, e o texto explica por quê, em vez de aceitar um clique que não faria
            nada.
          */}
          <div className="flex flex-wrap items-center justify-end gap-cx-3">
            <span className="text-cx-sm text-texto-suave" data-testid="contagem-pessoas-alterados">
              {alterados.length === 0
                ? 'Nenhuma alteração para salvar.'
                : alterados.length === 1
                  ? '1 alteração pendente.'
                  : `${alterados.length} alterações pendentes.`}
            </span>
            <Botao
              variante="primario"
              onClick={() => void salvarTudo()}
              disabled={alterados.length === 0 || salvando}
              data-testid="salvar-pessoas"
            >
              {salvando ? 'Salvando...' : 'Salvar alterações'}
            </Botao>
          </div>
        </>
      )}
    </div>
  );
}
